import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api, uitvoeren } from './client.ts';
import { useApiMutatie } from './mutaties.ts';
import type {
  CertificaatInvoer,
  IndelingInvoer,
  Kandidaat,
  KandidaatFilter,
  KandidaatRegistratie,
  KandidaatWijziging,
  OpleidingInvoer,
  TalenInvoer,
  VaardighedenInvoer,
  WensenInvoer,
  WerkervaringInvoer,
} from './typen.ts';

export const kandidaatSleutels = {
  alle: ['kandidaten'] as const,
  lijst: (filter: KandidaatFilter) => ['kandidaten', 'lijst', filter] as const,
  profiel: (id: string) => ['kandidaten', id] as const,
};

/**
 * Zoekt kandidaten op naam, e-mailadres of telefoonnummer, met filters.
 * @param filter De zoekterm, status, indeling en paginering.
 * @returns De query met de gevonden kandidaten.
 */
export function useKandidaten(filter: KandidaatFilter) {
  return useQuery({
    queryKey: kandidaatSleutels.lijst(filter),
    queryFn: () => uitvoeren(api.GET('/api/v1/kandidaten', { params: { query: filter } })),
    placeholderData: keepPreviousData,
  });
}

/**
 * Zoekt kandidaten voor een keuzelijst.
 * @param zoek De zoekterm.
 * @returns De gevonden kandidaten.
 */
export function zoekKandidaten(zoek: string): Promise<Kandidaat[]> {
  return uitvoeren(api.GET('/api/v1/kandidaten', { params: { query: zoek ? { zoek, limiet: 25 } : { limiet: 25 } } }));
}

/**
 * Haalt het volledige profiel van een kandidaat op.
 * @param id Het id van de kandidaat.
 * @returns De query met het profiel.
 */
export function useKandidaat(id: string | null | undefined) {
  return useQuery({
    queryKey: kandidaatSleutels.profiel(id ?? ''),
    queryFn: () => uitvoeren(api.GET('/api/v1/kandidaten/{id}', { params: { path: { id: id ?? '' } } })),
    enabled: Boolean(id),
  });
}

/**
 * De mutaties op een kandidaat en zijn wensen, indeling, vaardigheden en talen.
 * @returns De mutaties.
 */
export function useKandidaatMutaties() {
  const alle = () => [kandidaatSleutels.alle];
  const profiel = ({ id }: { id: string }) => [kandidaatSleutels.profiel(id), kandidaatSleutels.alle];
  return {
    registreer: useApiMutatie(
      (invoer: KandidaatRegistratie) => uitvoeren(api.POST('/api/v1/kandidaten', { body: invoer })),
      alle,
    ),
    wijzig: useApiMutatie(
      ({ id, invoer }: { id: string; invoer: KandidaatWijziging }) =>
        uitvoeren(api.PATCH('/api/v1/kandidaten/{id}', { params: { path: { id } }, body: invoer })),
      profiel,
    ),
    verwijder: useApiMutatie(
      (id: string) => uitvoeren(api.DELETE('/api/v1/kandidaten/{id}', { params: { path: { id } } })),
      () => [kandidaatSleutels.alle, ['voordrachten'], ['contactmomenten'], ['matches']],
    ),
    slaWensenOp: useApiMutatie(
      ({ id, invoer }: { id: string; invoer: WensenInvoer }) =>
        uitvoeren(api.PATCH('/api/v1/kandidaten/{id}/wensen', { params: { path: { id } }, body: invoer })),
      profiel,
    ),
    verwijderWensen: useApiMutatie(
      ({ id }: { id: string }) => uitvoeren(api.DELETE('/api/v1/kandidaten/{id}/wensen', { params: { path: { id } } })),
      profiel,
    ),
    zetIndeling: useApiMutatie(
      ({ id, invoer }: { id: string; invoer: IndelingInvoer }) =>
        uitvoeren(api.PUT('/api/v1/kandidaten/{id}/indeling', { params: { path: { id } }, body: invoer })),
      profiel,
    ),
    zetVaardigheden: useApiMutatie(
      ({ id, invoer }: { id: string; invoer: VaardighedenInvoer }) =>
        uitvoeren(api.PUT('/api/v1/kandidaten/{id}/vaardigheden', { params: { path: { id } }, body: invoer })),
      profiel,
    ),
    zetTalen: useApiMutatie(
      ({ id, invoer }: { id: string; invoer: TalenInvoer }) =>
        uitvoeren(api.PUT('/api/v1/kandidaten/{id}/talen', { params: { path: { id } }, body: invoer })),
      profiel,
    ),
  };
}

/**
 * De mutaties op de kwalificaties van een kandidaat: werkervaring, opleidingen en certificaten.
 * @param kandidaatId Het id van de kandidaat.
 * @returns Per soort kwalificatie toevoegen, wijzigen en verwijderen.
 */
export function useKwalificatieMutaties(kandidaatId: string) {
  const verversen = () => [kandidaatSleutels.profiel(kandidaatId)];
  const path = { path: { id: kandidaatId } };
  return {
    werkervaring: {
      voegToe: useApiMutatie(
        (invoer: WerkervaringInvoer) =>
          uitvoeren(api.POST('/api/v1/kandidaten/{id}/werkervaringen', { params: path, body: invoer })),
        verversen,
      ),
      wijzig: useApiMutatie(
        ({ id, invoer }: { id: string; invoer: Partial<WerkervaringInvoer> }) =>
          uitvoeren(api.PATCH('/api/v1/werkervaringen/{id}', { params: { path: { id } }, body: invoer })),
        verversen,
      ),
      verwijder: useApiMutatie(
        (id: string) => uitvoeren(api.DELETE('/api/v1/werkervaringen/{id}', { params: { path: { id } } })),
        verversen,
      ),
    },
    opleiding: {
      voegToe: useApiMutatie(
        (invoer: OpleidingInvoer) =>
          uitvoeren(api.POST('/api/v1/kandidaten/{id}/opleidingen', { params: path, body: invoer })),
        verversen,
      ),
      wijzig: useApiMutatie(
        ({ id, invoer }: { id: string; invoer: Partial<OpleidingInvoer> }) =>
          uitvoeren(api.PATCH('/api/v1/opleidingen/{id}', { params: { path: { id } }, body: invoer })),
        verversen,
      ),
      verwijder: useApiMutatie(
        (id: string) => uitvoeren(api.DELETE('/api/v1/opleidingen/{id}', { params: { path: { id } } })),
        verversen,
      ),
    },
    certificaat: {
      voegToe: useApiMutatie(
        (invoer: CertificaatInvoer) =>
          uitvoeren(api.POST('/api/v1/kandidaten/{id}/certificaten', { params: path, body: invoer })),
        verversen,
      ),
      wijzig: useApiMutatie(
        ({ id, invoer }: { id: string; invoer: Partial<CertificaatInvoer> }) =>
          uitvoeren(api.PATCH('/api/v1/certificaten/{id}', { params: { path: { id } }, body: invoer })),
        verversen,
      ),
      verwijder: useApiMutatie(
        (id: string) => uitvoeren(api.DELETE('/api/v1/certificaten/{id}', { params: { path: { id } } })),
        verversen,
      ),
    },
  };
}
